import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import { EPPAStep } from 'types/ppaOnboarding';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import { getStep, saveStep } from 'api/ppaOnboarding';
import { EInvitedProviderStep, INVITED_STEP_ROUTE, isInvitedProviderStep } from '../steps';
import ConfigureAvailability2 from 'pages/PPAOnboardingSteps/components/Steps/ConfigureAvailability/Step2';
import ConfigureAvailability3 from 'pages/PPAOnboardingSteps/components/Steps/ConfigureAvailability/Step3';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { EInvitedOnboardingStep } from '../types';

type SubStep = EPPAStep.STEP_15 | EPPAStep.STEP_16;

const SUB_STEP_ORDER: SubStep[] = [EPPAStep.STEP_15, EPPAStep.STEP_16];

const FORM_IDS: Record<SubStep, string> = {
    [EPPAStep.STEP_15]: 'invited-working-hours',
    [EPPAStep.STEP_16]: 'invited-advance-notice',
};

const AI_CHAT_BY_SUB_STEP: Record<SubStep, EAIChatPageId> = {
    [EPPAStep.STEP_15]: EAIChatPageId.INVITED_PROVIDER_AVAILABILITY_WORKING_HOURS,
    [EPPAStep.STEP_16]: EAIChatPageId.INVITED_PROVIDER_AVAILABILITY_ADVANCE_NOTICE,
};

const AvailabilityPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const cameFromBack = (location.state as { fromNext?: boolean } | null)?.fromNext === false;
    const [reviewData, , refetchReviewData] = usePPAOnboardingReviewInfo();
    const [subStep, setSubStep] = useState<SubStep>(cameFromBack ? EPPAStep.STEP_16 : EPPAStep.STEP_15);
    const [nextDisabled, setNextDisabled] = useState(false);

    useEffect(() => {
        setNextDisabled(false);
    }, [subStep]);

    useEffect(() => {
        if (cameFromBack) return undefined;
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled || !isInvitedProviderStep(saved)) return;
            if (saved === EInvitedProviderStep.AVAILABILITY_WORKING_HOURS) return;
            if (saved === EInvitedProviderStep.AVAILABILITY_ADVANCE_NOTICE) {
                setSubStep(EPPAStep.STEP_16);
                return;
            }
            const target = INVITED_STEP_ROUTE[saved];
            if (target && target !== PATH.INVITED_PROVIDER_ONBOARDING_AVAILABILITY) {
                navigate(target, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleSaveStep = useCallback(async () => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        refetchReviewData?.();
        if (idx < SUB_STEP_ORDER.length - 1) {
            await saveStep(EInvitedProviderStep.AVAILABILITY_ADVANCE_NOTICE);
            setSubStep(SUB_STEP_ORDER[idx + 1]);
        } else {
            await saveStep(EInvitedProviderStep.REVIEW);
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_REVIEW);
        }
    }, [subStep, navigate, refetchReviewData]);

    const handleBack = useCallback(() => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx > 0) {
            setSubStep(SUB_STEP_ORDER[idx - 1]);
        } else {
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_MARKETING_PROFILE, { state: { fromNext: false } });
        }
    }, [subStep, navigate]);

    const handleSkip = useCallback(async () => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx < SUB_STEP_ORDER.length - 1) {
            await saveStep(EInvitedProviderStep.AVAILABILITY_ADVANCE_NOTICE);
            setSubStep(SUB_STEP_ORDER[idx + 1]);
        } else {
            await saveStep(EInvitedProviderStep.REVIEW);
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_REVIEW);
        }
    }, [subStep, navigate]);

    const formId = FORM_IDS[subStep];

    const contentRender = useMemo(() => {
        if (subStep === EPPAStep.STEP_15) {
            return (
                <ConfigureAvailability2
                    formId={formId}
                    onSaveStep={handleSaveStep}
                    setNextButtonDisabled={setNextDisabled}
                />
            );
        }
        return (
            <ConfigureAvailability3
                formId={formId}
                onSaveStep={handleSaveStep}
                setNextButtonDisabled={setNextDisabled}
                initValue={reviewData?.calendarSetting}
            />
        );
    }, [subStep, formId, handleSaveStep, reviewData]);

    return (
        <Layout
            step={EInvitedOnboardingStep.AVAILABILITY}
            aiChatPageId={AI_CHAT_BY_SUB_STEP[subStep]}
            footer={
                <Footer
                    showBack
                    onBack={handleBack}
                    nextFormId={formId}
                    nextDisabled={nextDisabled}
                    onSkip={handleSkip}
                />
            }
        >
            {contentRender}
        </Layout>
    );
};

export default AvailabilityPage;
